<template>
  <fullscreen-dialog
    v-model="model"
    :title="dialogTitle"
    :subtitle="editedItem && editedItem.id ? 'ID: ' + editedItem.id : undefined"
    icon="mdi-file-document-edit"
    color="primary"
    :save-text="
      editedItem && editedItem.id
        ? $t('warehouse.update_offer')
        : $t('warehouse.send_offer')
    "
    :disabled="!valid || !editedItem.groceries.length"
    :loading="loading"
    @save="submitOffer"
  >
    <template v-slot:app-bar-actions>
      <excel-button class="mr-2" @click="exportExcel" />
      <print-button @click="print" />
    </template>

    <v-form ref="form" v-model="valid">
      <v-row>
        <v-col cols="12" lg="3" xl="2">
          <text-field
            :label="$t('warehouse.document_number_short')"
            v-model="editedItem.number"
            hide-details
            color="white"
          />
        </v-col>
        <v-col cols="12" lg="3" xl="2">
          <date-picker v-model="editedItem.document_date" single-date color="white" />
        </v-col>
        <v-col cols="12" lg="3" xl="2">
          <autocomplete
            v-model="editedItem.restaurant_id"
            :items="restaurantItems"
            :label="$t('restaurant')"
            item-text="name"
            item-value="id"
            hide-details
            :clearable="!editedItem.source_request_id"
            :disabled="!!editedItem.source_request_id"
            color="white"
          />
        </v-col>
        <v-col cols="12" lg="3" xl="2">
          <datetime-picker
            v-model="editedItem.delivery_deadline"
            single-date
            :label="$t('warehouse.delivery_deadline')"
            color="white"
          />
        </v-col>
        <v-col v-if="editedItem.source_request_id" cols="12" lg="2" xl="2">
          <text-field
            :label="$t('warehouse.request_quote_number')"
            :value="editedItem.source_request_number || editedItem.source_request_id"
            hide-details
            color="white"
            readonly
            disabled
          />
        </v-col>
        <v-col cols="12" lg="3" xl="4">
          <text-area
            :label="$t('note')"
            v-model="editedItem.notes"
            rows="1"
            color="white"
          />
        </v-col>
      </v-row>
    </v-form>

    <warehouse-document-groceries-table
      :groceries="editedItem.groceries"
      :warehouse="selectedWarehouse || {}"
      :headers="headers"
      :is-supplier="true"
      :title-text="$t('goods')"
      @removeGrocery="removeGrocery"
    />
  </fullscreen-dialog>
</template>

<script>
import hasWarehouseDocumentDialog from "@/mixins/warehouse/hasWarehouseDocumentDialog";
import hasPrintingV2 from "@/mixins/hasPrintingV2";
import hasWarehousePrinting from "@/mixins/warehouse/hasWarehousePrinting";
import hasExcel from "@/mixins/hasExcel";
import { orderDocumentGrocery } from "@/models/warehouse";
import warehouseService from "@/services/warehouse";

export default {
  name: "SupplierPortalOffersInvoicesDialog",
  mixins: [hasWarehouseDocumentDialog, hasPrintingV2, hasWarehousePrinting, hasExcel],
  components: {},
  props: {
    editedItem: {
      type: Object,
      default: () => ({ ...orderDocumentGrocery }),
    },
  },
  methods: {

    async submitOffer() {
      const user = this.$store.state.user.user;
      const supplier = user.suppliers?.[0] || null;

      const base = {
        restaurant_id: this.editedItem.restaurant_id,
        parent_id: this.editedItem.document_id || null,
        warehouse_id: this.editedItem.warehouse_id || null,
        document_date: this.editedItem.document_date,
        supplier_id: supplier?.id || null,
        restaurant_id: this.editedItem.restaurant_id || null,
        notes: this.editedItem.notes || null,
        status: "draft",
      };

      try {
        this.loading = true;
        if (this.editedItem.id) {
          await warehouseService.updateOffer(this.editedItem.id, {
            ...base,
            groceries: this.editedItem.groceries,
          });
        } else {
          await warehouseService.createOffer({
            ...base,
            groceries: this.editedItem.groceries.map((g) => ({
              grocery_id: g.id || g.grocery_id,
              amount: Number(g.amount) || 0,
              unit_price: Number(g.unit_price) || 0,
              gross_quantity: Number(g.gross_quantity) || 0,
              params: null,
            })),
          });
        }
        this.showSuccessSnackbar(this.$t("success"));
        this.model = false;
        this.$emit("success");
      } catch (error) {
        this.showErrorSnackbar(error, this.$t("error"));
      } finally {
        this.loading = false;
      }
    },
    print() {
      const { table, line, spacer, heading, text, row, col } = this.printElements;

      const docDate = this.localeDate(this.editedItem.document_date);
      const docNumber = this.editedItem.number || "";
      const docLabel = this.dialogTitle;

      const user = this.$store.state.user.user;
      const supplier = user.suppliers?.[0] || null;
      const orderer =
        (user.restaurants || []).find((r) => r.id === this.editedItem.restaurant_id) ||
        null;

      this.printDoc(
        [
          row("between", [
            this.printCompanyBlock(orderer, this.$t("warehouse.orderer")),
            this.printCompanyBlock(supplier, this.$t("supplier")),
          ]),
          spacer(14),
          heading(docLabel + (docNumber ? " " + docNumber : "")),
          `<div style="text-align:center;font-size:11px;color:#777;margin-top:2px">${docDate}</div>`,
          spacer(12),
          table(this.getPrintHeaders(false), this.getPrintItems()),
          spacer(24),
          text(
            this.$t("warehouse.delivery_deadline") +
              ": " +
              (this.editedItem.delivery_deadline
                ? this.localeDate(this.editedItem.delivery_deadline)
                : ""),
            { size: 13, block: true }
          ),
          spacer(40),
          row("end", [
            col("center", [
              text(this.$t("supplier")),
              spacer(16),
              line(),
            ]),
          ]),
        ],
        { pageTitle: docLabel + (docNumber ? " " + docNumber : "") }
      );
    },
    async exportExcel() {
      const docNumber = this.editedItem.number || "";
      const title = `${this.$t("warehouse.offers_invoices")} ${docNumber}`.trim();
      await this.downloadExcel({
        fileName: `${title}.xlsx`,
        sheets: [
          {
            name: title.substring(0, 31),
            title,
            headers: this.getPrintHeaders(false, false),
            items: this.getPrintItems(false),
          },
        ],
      });
    },
    async save() {
      const params = {
        ...this.editedItem,
        groceries: this.editedItem.groceries.map((grocery) => ({
          grocery_id: grocery.id,
          name: grocery.name,
          code: grocery.code,
          supplier_code: grocery.supplier_code,
          measure: grocery.measure,
          amount: grocery.amount,
          gross_quantity: Number(grocery.gross_quantity) || 0,
          unit_price: Number(grocery.unit_price) || 0,
          transaction_type: this.editedItem.document_type,
        })),
      };

      try {
        this.loading = true;
        const request = warehouseService.addDocumentTransaction(params);
        await this.handleRequest(request, {
          successMessage: this.$t("purchase_order_created_successfully"),
          errorMessagePrefix: this.$t("error"),
        });
        this.model = false;
        this.$emit("success");
      } catch (error) {
        console.error("Save operation failed:", error);
      } finally {
        this.loading = false;
      }
    },
  },
  computed: {
    dialogTitle() {
      if (this.editedItem?.document_type === "invoice")
        return this.$t("warehouse.invoice");
      return this.$t("warehouse.offer");
    },
    restaurantItems() {
      return this.$store.state.user.user?.restaurants || [];
    },
    filters() {
      return this.$store.state.warehouse.filters;
    },
    headers() {
      return [
        { text: this.$t("r_b"), value: "num", width: "100px" },
        { text: this.$t("code"), align: "start", value: "code", width: "150px" },
        { text: this.$t("supplier_code"), value: "supplier_code", width: "150px" },
        { text: this.$t("title"), value: "name" },
        {
          text: this.$t("quantity") + ` (${this.$t("warehouse.quantity_gross")})`,
          value: "gross_quantity",
          align: "right",
          width: "160px",
        },
        {
          text: this.$t("quantity") + ` (${this.$t("warehouse.quantity_net")})`,
          value: "amount",
          align: "right",
          width: "160px",
        },
        {
          text: this.$t("warehouse.warehouse_status"),
          value: "warehouse_stock",
          print: false,
          align: "right",
          width: "200px",
        },
        {
          text: this.$t("unit_of_measurment"),
          printText: this.$t("unit_of_measurment").replace(" ", "<br>"),
          value: "measure",
          width: "200px",
        },
        {
          text: this.$t("price"),
          value: "unit_price",
          align: "right",
          width: "140px",
        },
        {
          text: this.$t("pdv"),
          value: "pdv",
          align: "right",
          width: "140px",
        },
        {
          text: this.$t("amount"),
          value: "total_amount",
          align: "right",
          width: "140px",
          sortable: false,
        },
        { text: "", value: "actions", width: "100px" },
      ];
    },
  },
  watch: {
    model(newVal) {
      if (newVal && !this.editedItem.id) {
        const { from, to } = this.filters.dates || {};
        let note;
        if (this.editedItem.document_type === "offer") {
          note =
            from === to
              ? `Na osnovu zahtjeva za ponudu za ${from}`
              : `Na osnovu zahtjeva za ponudu za period od ${from} do ${to}`;
        } else {
          note =
            from === to
              ? `Po osnovu trebovanja za ${from}`
              : `Po osnovu trebovanja za period od ${from} do ${to}`;
        }
        this.editedItem.notes = this.translate(note);
      }
    },
  },
};
</script>
